import React, { useState, useEffect } from 'react';
import { Calendar, User, ArrowRight, Search, Tag, ExternalLink, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Blog posts from DMIN 851 document with updated dates
  const staticBlogPosts = [
    {
      id: 1,
      title: 'Remembering God\'s Word: Lessons from Deuteronomy 6',
      excerpt: 'In a world saturated with distractions, the call to remember God\'s Word is more urgent than ever. Explore how biblical memory practices intersect with cognitive science, offering practical steps for spiritual growth.',
      author: 'Sean Talluri',
      date: '2025-06-05',
      featured_image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Scripture', 'Memory', 'Discipleship'],
      tags: ['Deuteronomy 6', 'Bible Memory', 'Spiritual Growth'],
      slug: 'remembering-gods-word-deuteronomy-6'
    },
    {
      id: 2,
      title: 'Prayer as a Community Anchor',
      excerpt: 'Prayer wasn\'t an afterthought in the early church—it was the heartbeat of communal life. Discover how corporate prayer strengthens unity, faith, and spiritual resilience in Christian community.',
      author: 'Sean Talluri',
      date: '2025-07-05',
      featured_image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Prayer', 'Community', 'Church Life'],
      tags: ['Acts 1:14', '1 Thessalonians 5:16-18', 'Corporate Prayer'],
      slug: 'prayer-community-anchor'
    },
    {
      id: 3,
      title: 'Grace and Salvation – Understanding God\'s Gift',
      excerpt: 'Grace and salvation are the heartbeat of the gospel. Understand how God\'s unmerited favor transforms our relationship with Him and reshapes how we live.',
      author: 'Sean Talluri',
      date: '2025-08-05',
      featured_image: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Grace', 'Salvation', 'Gospel'],
      tags: ['Ephesians 2:8-9', 'Romans 6:23', 'Salvation'],
      slug: 'grace-salvation-gods-gift'
    },
    {
      id: 4,
      title: 'Faith in Trials — Overcoming Challenges with God\'s Strength',
      excerpt: 'Life\'s journey is filled with unexpected trials, yet Scripture reframes these challenges as pathways to spiritual maturity. Discover how faith grows stronger through adversity.',
      author: 'Sean Talluri',
      date: '2025-09-05',
      featured_image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Faith', 'Trials', 'Perseverance'],
      tags: ['James 1:2-4', 'Romans 5:3-5', '1 Peter 1:6-7'],
      slug: 'faith-in-trials-gods-strength'
    },
    {
      id: 5,
      title: 'Christ at Work – Demonstrating Faith in the Marketplace',
      excerpt: 'Most believers spend the majority of their waking hours at work, yet often compartmentalize spirituality. Learn how to authentically live out Christ in your workplace through excellence, integrity, and witness.',
      author: 'Sean Talluri',
      date: '2025-10-05',
      featured_image: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Faith at Work', 'Marketplace Ministry', 'Witness'],
      tags: ['Colossians 3:23-24', 'Genesis 2:15', '1 Peter 2:12'],
      slug: 'christ-at-work-marketplace-faith'
    },
    {
      id: 6,
      title: 'Living Out Scripture – Integrating the Word into Everyday Discipleship',
      excerpt: 'The Bible isn\'t meant to be admired from a distance—it\'s to shape us from the inside out. Learn practical ways to embed Scripture into everyday discipleship through intentional habits and community.',
      author: 'Sean Talluri',
      date: '2025-11-05',
      featured_image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=400&h=250&auto=format&fit=crop',
      categories: ['Scripture', 'Discipleship', 'Christian Living'],
      tags: ['Psalm 119:11', 'Colossians 3:16', '2 Timothy 3:16-17'],
      slug: 'living-out-scripture-everyday-discipleship'
    }
  ];

  const categories = ['all', 'Scripture', 'Prayer', 'Grace', 'Faith', 'Community', 'Discipleship', 'Salvation'];

  useEffect(() => {
    setBlogPosts(staticBlogPosts);
  }, []);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           post.categories.some(cat => cat.toLowerCase() === selectedCategory.toLowerCase());
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Recent Teaching & Insights
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 font-sans">
              Biblical teaching and spiritual encouragement from El Bethel Church. 
              Explore recent posts or visit our online ministry for the full archive.
            </p>
            
            {/* Full Archive Link */}
            <div className="max-w-xl mx-auto bg-white border-2 border-[#f17b21] rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BookOpen className="w-6 h-6 text-[#f17b21]" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">Looking for more content?</p>
                    <p className="text-sm text-gray-600">Browse our complete archive online</p>
                  </div>
                </div>
                <a 
                  href="https://elbethel.online/blog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center space-x-1 whitespace-nowrap"
                >
                  <span>Visit Online</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans bg-white"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredPosts.map((post) => (
              <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="relative h-48 lg:h-56 overflow-hidden">
                  <img 
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    {post.categories.slice(0, 2).map((category, index) => (
                      <span key={index} className="bg-[#f17b21] text-white px-2 py-1 rounded text-xs font-semibold mr-2 font-sans">
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-3 font-display hover:text-[#f17b21] transition-colors duration-300">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed font-sans text-sm lg:text-base line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4 font-sans">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.date)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button 
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="flex items-center space-x-2 text-[#f17b21] hover:text-[#d86919] font-semibold transition-colors duration-300 font-sans"
                    >
                      <span>Read Full Post</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 font-semibold transition-colors duration-300 font-sans"
                    >
                      <BookOpen className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 font-sans">No blog posts found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6 font-display">
              Stay Updated
            </h2>
            <p className="text-lg text-gray-600 mb-8 font-sans">
              Receive our latest blog posts and spiritual insights directly in your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
              />
              <button className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;